import dynamic from 'next/dynamic';
import { StylesConfig } from 'react-select';

const Select = dynamic(() => import('react-select'), {
  ssr: false,
  loading: () => (<div className='flex items-center min-h-[48px] rounded-[2rem] bg-white border border-shade-100 animate-pulse'>
    <div className='flex items-center pl-6 mr-auto py-1'>
      <div className='h-5 w-24 bg-shade-100 rounded-full' />
    </div>
  </div>), // Optional loading state
});

export type TDropdownOption = {
  label: string,
  value: string,
} | undefined;

export type TDropdownOptions = TDropdownOption[];

interface IDropdownProps {
  onChange?: (newValue: { value: string; label: string; }) => void,
  options: TDropdownOptions,
  value: TDropdownOption | null,
  placeholder?: string,
  id: string,
  isMulti?: boolean,
  isSearchable?: boolean,
  errorMessage?: string,
  showErrorMessage?: boolean,
  label?: string,
  className?: string,
  closeMenuOnSelect?: boolean,
}

export default function Dropdown({
  options,
  value,
  placeholder,
  onChange,
  id,
  isMulti,
  isSearchable = false,
  errorMessage,
  showErrorMessage,
  label,
  className,
  closeMenuOnSelect = true,
}: IDropdownProps) {
  const emitOnChange = onChange;
  // Custom styling for react-select, see: https://react-select.com/styles
  const styles: StylesConfig = {
    control: (provided, state) => ({
      display: 'flex',
      alignItems: 'center',
      minHeight: '48px',
      borderRadius: '2rem',
      backgroundColor: 'white',
      border: showErrorMessage ? '1px solid var(--color-error)' : '1px solid var(--color-shade-100)',
      cursor: 'pointer',
      fontSize: '1rem',
      lineHeight: '1.5rem',
      ...(state.menuIsOpen && {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
      }),
      '&:hover': {
        borderColor: 'var(--color-primary-200)',
      },
      '&:focus': {
        borderColor: 'var(--color-primary-200)',
      },
    }),
    valueContainer: () => ({
      display: 'flex',
      alignItems: 'center',
      paddingLeft: '1.5rem',
      marginRight: 'auto',
      paddingTop: '0.25rem',
      paddingBottom: '0.25rem',
      ...(isMulti && {
        maxWidth: '90%',
        flexWrap: 'wrap' as const,
      }),
    }),
    singleValue: () => ({
      fontSize: '1rem',
      lineHeight: '1.5rem',
      position: 'relative' as const,
    }),
    indicatorsContainer: () => ({
      display: 'flex',
      alignItems: 'center',
      paddingRight: '1rem',
    }),
    dropdownIndicator: () => ({
      display: 'flex',
    }),
    menu: () => ({
      position: 'absolute' as const,
      paddingTop: '0.5rem',
      paddingBottom: '0.5rem',
      backgroundColor: 'white',
      width: '100%',
      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
      borderBottomLeftRadius: '2rem',
      borderBottomRightRadius: '2rem',
      overflow: 'hidden',
      border: '1px solid var(--color-shade-100)',
      borderTop: 0,
    }),
    menuList: () => ({
      maxHeight: '280px',
      overflowY: 'auto' as const,
      overflowX: 'hidden' as const,
    }),
    option: (provided, state) => ({
      paddingLeft: '1.5rem',
      paddingRight: '1.5rem',
      paddingTop: '0.5rem',
      paddingBottom: '0.5rem',
      backgroundColor: 'white',
      fontSize: '1rem',
      lineHeight: '1.5rem',
      cursor: 'pointer',
      ...(state.isSelected && {
        color: 'var(--color-primary)',
        fontWeight: 600,
      }),
      '&:hover': {
        backgroundColor: 'var(--color-primary-50)',
      },
      '&:focus': {
        backgroundColor: 'var(--color-primary-50)',
      },
    }),
    placeholder: () => ({
      color: 'var(--color-shade-700)',
      fontSize: '1rem',
      lineHeight: '1.5rem',
      fontFamily: 'var(--font-default)',
    }),
    multiValue: (provided) => ({
      ...provided,
      border: '1px solid var(--color-shade-200)',
      backgroundColor: 'var(--color-shade-50)',
      borderRadius: '1rem',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      margin: '0.25rem',
      '&:hover': {
        backgroundColor: 'var(--color-error-100)',
        color: 'var(--color-error)',
      },
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      fontFamily: 'var(--font-default)',
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
    }),
  };

  return (
    <div className={ className }>
      { label &&
        <div className='flex items-center mb-1'>
          <label htmlFor={ `react-select-${ id }-input` } className='text-body-md text-body-md--semibold' data-error={ showErrorMessage && errorMessage ? 'true' : 'false' }>{ label }</label>
        </div>
      }
      <Select
        key={ id }
        id={ id }
        isMulti={ isMulti }
        closeMenuOnSelect={ closeMenuOnSelect }
        instanceId={ id }
        options={ options }
        onChange={ emitOnChange }
        value={ value }
        classNamePrefix={ 'react-select' }
        isSearchable={ isSearchable }
        placeholder={ placeholder ?? null }
        components={ { IndicatorSeparator: () => null } }
        styles={ styles }
        aria-label={ id }
        isClearable={ true }
      />
      {
        errorMessage && errorMessage.length > 0 && showErrorMessage &&
        <span className='block w-full text-body-sm mt-2 text-error md:text-left'>{errorMessage}</span>
      }
    </div>
  );
}
